<template lang="pug">
.SongDetailHeader
	.img-wrap
		img(:src="image" width="253" height="253" :alt="`${song.name} - Karaoke`")
	.detail-info-wrap
		.detail-title
			.top-wrap
				h1.title {{ song.name }}
					span.title-karaoke Karaoke
				span.title-sub(v-for="(artist, i) in props.song.artists" :key="i")
					NuxtLink(:to="localePath(`/artists/${artist.slug}/${artist.hash}/`)")
						| {{ artist.name }}
						span(v-if="i+1 != props.song.artists.length") ,&nbsp;
			SongTags(:tags="songTags" variant="detail")

		.detail-info-bottom
			.left
				SingaButton.is-small.is-primary.play-button(icon-left="play" @click="playSong(song, false, t('song.error.play'))" :disabled="showGeoBlocked || hasNoVariants") {{ t('general.sing') }}
				SingaButton.is-small.is-transparent-dark(v-if="isUserAuthorised" @click="addSongToQueue(song)" :disabled="showGeoBlocked || hasNoVariants") {{ t('song.addToQueue') }}
				CommonShareMenu
				template(v-if="isUserAuthorised && !showGeoBlocked && !hasNoVariants")
					SongFavoriteButton(:id="props.song.id" :resourceId="song.resource_id" :title="props.song.name" :link="favoritesLink" :standalone="true" accessPoint="Song detail header")
					SongContextMenu(:song="song" :link="link" :isSongDetailPage="true" :id="props.song.id" accessPoint="Song detail header")
			span.not-available-text(v-if="showGeoBlocked") {{ t('song.notAvailable.detail') }}
			span.not-available-text(v-if="hasNoVariants") {{ t('song.notAvailable.detail.variants') }}
</template>

<script setup lang="ts">
import { FastAverageColor } from 'fast-average-color'
import type { Color, Song } from '~~/types'
import { usePlaySong } from '~/composables/useQueue'

const playSong = usePlaySong

const { t } = useI18n()
const localePath = useLocalePath()

const { isUserAuthorised } = useUser()

const useImage = useGetImageSize()

const queueStore = useQueueStore()
const { addToQueue } = queueStore
const { queueItemSelection } = useVariantSelection()

const props = defineProps({
	song: {
		type: Object as PropType<Song>,
		required: true
	}
})

const route = useRoute()
const favoritesLink = route.fullPath
const { getArtistSlugs } = useArtistNames(props.song.artists)
const artistSlug = getArtistSlugs()
const link = `/artists/${artistSlug}/${props.song.slug}/${props.song.hash}/`

const image = useImage(props.song.image, 'small', 'song', true)

const songTags = {
	has_cover: props.song.has_cover,
	has_duet: props.song.has_duet,
	has_explicit: props.song.has_explicit,
	has_original: props.song.has_original,
	has_plus: props.song.has_plus
}

const showGeoBlocked = ref(false)

const addSongToQueue = async (song: Song) => {
	if (!song) {
		console.warn('SongListItem: Trying to queue undefined song')
		return
	}
	const queueItem = await queueItemSelection(song, undefined, undefined, t('song.error.play'))
	if (!queueItem) { return }
	await addToQueue(queueItem, link, 'Song list row item button', false, t('song.error.queue'))
}

const hasNoVariants = computed(() => {
	return props.song.variants.length === 0
})

onMounted(() => {
	if (props.song.image) {
		const fac = new FastAverageColor()
		fac.getColorAsync(image).then(color =>
			useEventEmit('layoutGradient:applyColor', color.hex as Color)
		).catch(e =>
			console.error(e)
		)
	}
	if (props.song.geoblocked) {
		showGeoBlocked.value = true
	}
})

onUnmounted(() => {
	useEventEmit('layoutGradient:resetDefault')
})
</script>

<style lang="sass" scoped>
.SongDetailHeader
	display: flex
	flex-wrap: wrap
	flex-direction: column
	align-items: flex-start
	@media (min-width: 865px)
		flex-direction: row

.right
	display: flex
	> *:not(:last-child)
		margin-right: $spacing-24
.img-wrap
	width: 160px
	height: 160px
	background-color: $transparent-white-4
	display: flex
	flex-shrink: 0
	align-self: center
	box-shadow: $shadow-default
	margin-bottom: $spacing-24
	border-radius: $radius-default
	overflow: hidden
	img
		width: 100%
	@media (min-width: $tablet)
		margin-right: $spacing-32
		margin-bottom: 0
		width: 253px
		height: 253px
h1.title
	@include fontSize(xxl)
	@include font(basier, bold)
	margin-bottom: 0
	color: $color-grey-30
	text-overflow: ellipsis
	display: block
	white-space: nowrap
	overflow: hidden
	@media (min-width: $tablet)
		font-size: 40px !important
		line-height: 64px !important
.title-sub
	@include font(basier, semiBold)
	@include fontSize(s)
	color: $color-grey-60
	a
		color: $color-grey-60
	@media (min-width: $tablet)
		font-size: 24px
		line-height: 36px
.detail-info
	color: $transparent-white-40
.detail-title
	display: flex
	justify-content: space-between
	flex-direction: column
	@media (min-width: $tablet)
		flex-direction: row
		margin-bottom: 28px

.top
	display: flex
.bottom
	display: flex
.detail-info-bottom
	display: flex
	flex-wrap: wrap
	.left
		width: 100%
		>:not(.play-button)
			display: none
		.play-button
			margin-right: 0
			width: 100%
		display: flex
		flex-wrap: wrap
		> *
			display: flex
			flex: 1
		> button:not(:first-child)
			display: none
		button
			width: 100%
			margin-right: 0

	@media (min-width: $mobile-small)
		.left
			width: auto
			>:not(.play-button)
				display: flex
			> *:not(:last-child)
				margin-right: $spacing-16
	@media (min-width: 865px)
		> .left button:not(:first-child)
			display: flex
		display: flex
		justify-content: space-between
		align-items: flex-end

.detail-info-wrap
	display: flex
	flex-direction: column
	justify-content: space-between
	width: 100%
	@media (min-width: $tablet)
		width: calc(100% - 285px)
.filter-icon
	margin-right: $spacing-8 !important
.SongTags
	flex-shrink: 0
	margin: 12px 0 $spacing-24
	@media (min-width: $tablet)
		margin: $spacing-16 0 $spacing-24
.top-wrap
	min-width: 0
	padding-right: $spacing-16
.not-available-text
	display: block
	color: $color-grey-60
	@include fontSize(m)
	padding-top: $spacing-16
	width: 100%
.play-button:disabled
	background-color :$transparent-white-8
	color: $transparent-white-24
	border: none
</style>

<style lang="sass">
.detail-like
	path
		stroke: $color-grey-30
.SongDetailHeader
	.play-button.is-small
		padding-top: 7px
		padding-right: 32px
		padding-bottom: 7px
		padding-left: 32px
		.icon.is-small
			width: 20px
			margin-right: $spacing-8
.title-karaoke
	@include font(basier, bold)
	color: $color-grey-70
	padding-left: $spacing-8
	letter-spacing: .4px !important
</style>
